<template>
  <!--
    「요금·결제」의 이용 기간 늘리기 창(설계안 §25.9 — 장애 보상 같은 까닭으로 결제로 이용 중인 구독에 날을 더해 다음 결제를 미룬다). 지금 이용
    끝·산 기간의 끝(상세 `extend` — 서버 값)을 보이고 새 이용 끝·사유를 받는다. 새 끝은 지금보다 뒤이고 산 기간의 끝보다 앞은 안 된다(앞당기기는
    거기까지 — 잘못 준 보상 되돌리기). 셈은 서버 — 결제 끝·다음 결제·중도 해지 셈이 늘린 끝을 따른다. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(text.extend)"
    :visible="visible"
    width="440px"
    append-to-body
    data-testid="subscription-extend-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-extend__store">{{ storeLabel }}</p>
    <dl class="subscription-extend__rows" data-testid="subscription-extend-rows">
      <template v-for="row in rows">
        <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
        <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
      </template>
    </dl>
    <el-form label-position="top" @submit.native.prevent>
      <el-form-item :label="$t(text.extendNew)" :error="problems.picked">
        <SubscriptionMomentPicker
          v-model="form.picked"
          :floor-ms="floorMs"
          data-testid="subscription-extend-picker"
          @change="problems.picked = ''" />
      </el-form-item>
      <el-form-item :label="$t(text.reason)" :error="problems.reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="2"
          data-testid="subscription-extend-reason"
          :placeholder="$t(text.reasonPlaceholder)"></el-input>
      </el-form-item>
    </el-form>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button size="small" type="primary" data-testid="subscription-extend-submit" :loading="busy" @click="submit">{{
        $t(text.trialEndSubmit)
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import SubscriptionMomentPicker from '@/components/subscription/SubscriptionMomentPicker.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  extendFloorMs,
  extendRequestOf,
  extendRowsOf,
} from '@/utils/subscriptionAdmin'

const noProblems = () => ({ picked: '', reason: '' })

export default {
  name: 'SubscriptionExtendDialog',

  components: { SubscriptionMomentPicker },

  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    /** 요청할 구독 — 상세 창이 가진 ID(상세를 다시 읽는 동안·읽기 실패에도 그대로다) */
    subscriptionId: { type: Number, default: null },
    /** 상세의 `extend`(`{chargeId, periodEnd, usableUntil}` — 서버가 늘릴 수 있다고 할 때만) — 다시 읽는 동안은 `null` */
    extend: { type: Object, default: null },
    /** 매장 이름(「QA 피자 · S2600…」) */
    storeLabel: { type: String, default: '' },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      form: { picked: '', reason: '' },
      /** 연 때의 아래 끝(산 기간의 끝과 지금 중 늦은 것) — 달력이 그 날보다 앞을 막는다 */
      floorMs: null,
      problems: noProblems(),
      busy: false,
    }
  },

  computed: {
    rows: function () {
      return extendRowsOf(this.extend)
    },
  },

  methods: {
    /** 열 때마다 새로 — 아래 끝을 정하고 고른 값·사유·문장을 비운다(시계는 여기서 읽는다) */
    reset: function () {
      this.floorMs = extendFloorMs(this.extend, Date.now())
      this.form = { picked: '', reason: '' }
      this.problems = noProblems()
      this.busy = false
    },

    /**
     * 늘리기 — 보내고, 말하고, 부모가 상세·목록을 다시 읽게 한다. 거절(400 산 기간의 끝 앞·지난 시각 · 409 `NOT_ACTIVE`·`CHARGE_PENDING`·
     * `STATUS_CHANGED`)은 서버 문장을 창 안에서 한 번 — 409 면 창을 열어 둔 채 다시 읽는다.
     */
    submit: async function () {
      const request = extendRequestOf(this.subscriptionId, this.extend, this.form, Date.now())
      this.problems = noProblems()
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      this.busy = true
      try {
        await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)

        this.$emit('update:visible', false)
        this.mainVue.$message({ type: 'success', message: this.$t(this.text.extendDone) })
        this.$emit('changed')
      } catch (error) {
        this.problems.picked = httpErrorMessage(error, this.$t(this.text.writeFailed))
        if (error && error.status === 409) this.$emit('changed')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-extend__store {
  margin: 0 0 8px;
  color: var(--color-text-regular);
}

.subscription-extend__rows {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 4px 16px;
  margin: 0 0 12px;
}

.subscription-extend__rows dt {
  color: var(--color-text-secondary);
}

.subscription-extend__rows dd {
  margin: 0;
  color: var(--color-text-primary);
}
</style>
